import {Injectable, Inject, ErrorHandler, Injector, NgZone} from '@angular/core';
import { MessageService } from './message.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CONSTANTS } from '../../config/constants.module';
import {StartupService} from './startup.service';
import Enums from '../../shared/enum/enums';
import {Router} from '@angular/router';

enum LayerError {
  COUNT_FEATURES_FROM_LAYER_FAILED,
  GET_FEATURES_FROM_LAYER_FAILED,
  GET_FEATURES_FROM_LAYER_WITH_GEOMETRY,
  GET_FEATURES_FROM_RELATION_TABLE,
  GET_FIELDS_FROM_LAYER,
}
/**
 * @class ErrorHandlingService
 * @version 0.0.1
 * @since October 2017
 * @author Tuan La
 * @description The ErrorHandlingService handles server responses.
 */
@Injectable({
  providedIn: 'root',
})

export class ErrorHandlingService implements ErrorHandler {
  _httpErrors: HttpErrorResponse[];
  static LayerError: typeof LayerError = LayerError;

  public get errors(): HttpErrorResponse[] {
    return this._httpErrors;
  }

  constructor(
    @Inject(MessageService) private _messageService: MessageService,
    @Inject(CONSTANTS) private CONST: any,
    private readonly router: Router,
    private readonly zone: NgZone,
    private readonly injector: Injector,
  ) {
    this._httpErrors = new Array<HttpErrorResponse>();
  }

  public handleError(error: HttpErrorResponse | Error | any) {
    const startupService = this.injector.get(StartupService);

    // unwrapp error from zone.js if error is wrapped
    const err = error.rejection ? error.rejection : error;
    if (err instanceof HttpErrorResponse) {
      // Esri Api loading failed
      if (
        err.status === Enums.HttpStatus.SBB_LOGIN_ERROR_999 ||
        err.status === Enums.HttpStatus.ESRI_JS_API_LOAD_ERROR_998
      ) {
        this.zone.run(() => this.router.navigate(['/error']));
        if (!startupService.errors || !startupService.errors.length) {
          startupService.errors.push(err.statusText);
        }
      }
    }

    throw error;
  }

  handleBadHttpResponse(response?: Response, httpStatus?: Response): void {
    const status = httpStatus ? httpStatus : response.status;
    switch (status) {
      case this.CONST.HTTP_STATUS.NO_CONTENT_204:
        this._messageService.error('NOTIFICATIONS.ERROR.NO_CONTENT_204');
        break;
      case this.CONST.HTTP_STATUS.NOT_MODIFIED_304:
        this._messageService.error('NOTIFICATIONS.ERROR.NOT_MODIFIED_304');
        break;
      case this.CONST.HTTP_STATUS.BAD_REQUEST_400:
        this._messageService.error('NOTIFICATIONS.ERROR.BAD_REQUEST_400');
        break;
      case this.CONST.HTTP_STATUS.UNAUTHORIZED_401:
        this._messageService.error('NOTIFICATIONS.ERROR.UNAUTHORIZED_401');
        break;
      case this.CONST.HTTP_STATUS.FORBIDDEN_403:
        this._messageService.error('NOTIFICATIONS.ERROR.FORBIDDEN_403');
        break;
      case this.CONST.HTTP_STATUS.NOT_FOUND_404:
        this._messageService.error('NOTIFICATIONS.ERROR.NOT_FOUND_404');
        break;
      case this.CONST.HTTP_STATUS.METHOD_NOT_ALLOWED_405:
        this._messageService.error('NOTIFICATIONS.ERROR.METHOD_NOT_ALLOWED_405');
        break;
      case this.CONST.HTTP_STATUS.INTERNAL_SERVER_ERROR_500:
        this._messageService.error('NOTIFICATIONS.ERROR.INTERNAL_SERVER_ERROR_500');
        break;
      default:
        break;
    }
  }

  public getHttpErrorResponseTranslationKey(errResponse: HttpErrorResponse): string {
    const status = errResponse.status;
    let errorKey = '';
    switch (status) {
      case this.CONST.HTTP_STATUS.NO_CONTENT_204:
        errorKey = 'NOTIFICATIONS.ERROR.NO_CONTENT_204';
        break;
      case this.CONST.HTTP_STATUS.NOT_MODIFIED_304:
        errorKey = 'NOTIFICATIONS.ERROR.NOT_MODIFIED_304';
        break;
      case this.CONST.HTTP_STATUS.BAD_REQUEST_400:
        errorKey = 'NOTIFICATIONS.ERROR.BAD_REQUEST_400';
        break;
      case this.CONST.HTTP_STATUS.UNAUTHORIZED_401:
        errorKey = 'NOTIFICATIONS.ERROR.UNAUTHORIZED_401';
        break;
      case this.CONST.HTTP_STATUS.FORBIDDEN_403:
        errorKey = 'NOTIFICATIONS.ERROR.FORBIDDEN_403';
        break;
      case this.CONST.HTTP_STATUS.NOT_FOUND_404:
        errorKey = 'NOTIFICATIONS.ERROR.NOT_FOUND_404';
        break;
      case this.CONST.HTTP_STATUS.METHOD_NOT_ALLOWED_405:
        errorKey = 'NOTIFICATIONS.ERROR.METHOD_NOT_ALLOWED_405';
        break;
      case this.CONST.HTTP_STATUS.INTERNAL_SERVER_ERROR_500:
        errorKey = 'NOTIFICATIONS.ERROR.INTERNAL_SERVER_ERROR_500';
        break;
      case this.CONST.HTTP_STATUS.SBB_LOGIN_ERROR:
        errorKey = 'NOTIFICATIONS.ERROR.SBB_LOGIN_ERROR';
        break;
      case this.CONST.HTTP_STATUS.ESRI_JS_API_LOAD_ERROR:
        errorKey = 'NOTIFICATIONS.ERROR.ESRI_JS_API_LOAD_ERROR';
        break;
      default:
        errorKey = 'NOTIFICATIONS.ERROR.APP_LOADING_FAIL';
        break;
    }
    return errorKey;
  }

  handleLayerError(error: any): void {
    this.handleBadHttpResponse(null, error.response.details.httpStatus);
    switch (error.type) {
      case this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_FAILED:
        this._messageService.error('NOTIFICATIONS.ERROR.LAYER.GET_FEATURES_FROM_LAYER_FAILED');
        break;
      case this.CONST.LAYER_ERROR.COUNT_FEATURES_FROM_LAYER_FAILED:
        this._messageService.error('NOTIFICATIONS.ERROR.LAYER.COUNT_FEATURES_FROM_LAYER_FAILED');
        break;
      case this.CONST.LAYER_ERROR.GET_FEATURES_FROM_LAYER_WITH_GEOMETRY:
        this._messageService.error(
          'NOTIFICATIONS.ERROR.LAYER.GET_FEATURES_FROM_LAYER_WITH_GEOMETRY'
        );
        break;
      case this.CONST.LAYER_ERROR.GET_FEATURES_FROM_RELATION_TABLE:
        this._messageService.error('NOTIFICATIONS.ERROR.LAYER.GET_FEATURES_FROM_RELATION_TABLE');
        break;
      case this.CONST.LAYER_ERROR.GET_FIELDS_FROM_LAYER:
        this._messageService.error('NOTIFICATIONS.ERROR.LAYER.GET_FIELDS_FROM_LAYER');
        break;
      default:
        break;
    }
  }

  handleSoeError(error: any): void {
    this.handleBadHttpResponse(null, error.details.httpStatus);
  }

  public clearInternalErrorStack(): void {
    this._httpErrors = new Array<HttpErrorResponse>();
  }
}
